'use client';

import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { useRole } from '@/contexts/RoleContext';
import MobileSalesRepView from './mobile/MobileSalesRepView';
import MobileDriverView from './mobile/MobileDriverView';

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  role?: 'supplier' | 'retailer' | 'hybrid';
}

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

export default function AppLayout({ children, title, subtitle, role = 'hybrid' }: AppLayoutProps) {
  const { activeStaff, isRoleAuthenticated } = useRole();
  const isMobile = useIsMobile();

  // On mobile, show dedicated views for field roles
  if (isMobile && isRoleAuthenticated && activeStaff) {
    if (activeStaff.roleType === 'Sales_Rep') {
      return <MobileSalesRepView />;
    }
    if (activeStaff.roleType === 'Driver') {
      return <MobileDriverView />;
    }
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar role={role} />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar title={title} subtitle={subtitle} role={role} />
        <main className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 xl:px-8 2xl:px-10 py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}